import { ComponentProps, useCallback, useMemo, useState } from 'react'
import View, { DeliveryMethod, PaymentMethod } from './DigitalTwin.view'
import { DigitalTwinBuyStep, DigitalTwinProps } from './DigitalTwin.types'
import { OriginAddressSelection } from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.view'
import { useAddresses } from 'context/User'
import { Address } from 'features/Form/AddressForm/AddressForm.types'
import AsyncWrapper from '@onepercentio/one-ui/components/AsyncWrapper'
import { useHistory } from 'react-router-dom'
import { t } from 'translate/i18n'
import { getQuotations } from 'core/modules/firebase/service/logistics'
import { DeliveryQuotation } from '@onepercentio/rarum-types/logistics'
import { useScrollWhenEffect } from 'containers/MainRouter/MainRouter.hook'
import { OriginAddressSelectionWrapper } from 'pages/Authenticated/Marketplace/CreateOffer/OfferType/DigitalTwin/DigitalTwin.logic'

export default function DigitalTwinLogic({
  asset,
  sale,
  onBuyOffer,
}: DigitalTwinProps) {
  const [step, setStep] = useState(DigitalTwinBuyStep.DELIVERY_ADDRESS)
  const { goBack } = useHistory()
  const [selectedAddress, setSelectedAddress] = useState<Address>()
  const [selectedQuote, setQuote] = useState<DeliveryQuotation>()
  const onContinue = useCallback(() => {
    if (step === DigitalTwinBuyStep.PAYMENT_METHOD) {
      return onBuyOffer(selectedAddress!, selectedQuote!)
    } else {
      setStep((prev) => prev + 1)
    }
  }, [step, selectedAddress])
  useScrollWhenEffect(step)
  const content = (() => {
    switch (step) {
      case DigitalTwinBuyStep.DELIVERY_ADDRESS:
        return (
          <OriginAddressSelectionWrapper
            onSelectOriginAddress={setSelectedAddress}
            selectedAddress={selectedAddress}
            canContinue={!!selectedAddress}
            onCancel={goBack}
            onContinue={onContinue}
            continueLabel={t(
              'marketplace.buyOffer.digitalTwin.resume.paymentMethod.actions.useAddress'
            )}
            mode='buy'
          />
        )
      case DigitalTwinBuyStep.DELIVERY_METHOD:
        return (
          <DeliveryMethodWrapper
            deliveryAddress={selectedAddress!}
            onSetMethod={setQuote}
            method={selectedQuote}
            canContinue={!!selectedQuote}
            onBack={() => {
              setStep((prev) => prev - 1)
            }}
            onContinue={onContinue}
            sale={sale}
            continueLabel={t('generic.continue')}
          />
        )
      case DigitalTwinBuyStep.PAYMENT_METHOD:
        return (
          <PaymentMethod
            deliveryAddress={selectedAddress!}
            asset={asset}
            price={sale.price}
            canContinue
            onContinue={onContinue}
            continueLabel={t(
              'marketplace.buyOffer.digitalTwin.resume.actions.pay'
            )}
            quote={selectedQuote!}
          />
        )
    }
  })()
  return (
    <AsyncWrapper
      loading={false}
      error={false}
      onClose={() => goBack()}
      onRetry={() => {}}>
      <View currentStep={step}>{content}</View>
    </AsyncWrapper>
  )
}

function DeliveryMethodWrapper(
  props: Omit<
    ComponentProps<typeof DeliveryMethod>,
    'onLoaded' | 'availableMethods' | 'loadQuotes'
  >
) {
  const { deliveryAddress, sale } = props
  const [quotations, setQuotations] = useState<DeliveryQuotation[]>()
  return (
    <DeliveryMethod
      {...props}
      loadQuotes={() =>
        getQuotations(deliveryAddress, sale).then(setQuotations)
      }
      availableMethods={quotations}
    />
  )
}
